import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { G, TIMING } from "./geometry.js";
import { PandaMark } from "./PandaMark.js";
import "./panda-mark.css";

const INK = "#000000";
const SURFACE = "#FFFFFF";
const ALL_ON = { blink: true, breathe: true, twitch: true, track: true, greet: true, hover: true };
const rand = (a, b) => a + Math.random() * (b - a);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/**
 * Animated PandaTaps mark. Static mark is complete on its own; this adds a
 * CSS/transform layer. Disabled below 48px and under reduced motion.
 *
 * Props: size, animate, onEvent(type), behaviours {blink,twitch,track,greet,hover},
 * reducedMotion (force), hoverable, ink, face, title, ref (imperative triggers).
 */
export function PandaMarkLive({
  size = 200,
  animate = true,
  onEvent = undefined,
  behaviours = ALL_ON,
  reducedMotion = false,
  hoverable = true,
  ink = INK,
  face = SURFACE,
  title = undefined,
  ref = undefined,
}) {
  const svgRef = useRef(null);
  const trackRef = useRef(null);
  const timers = useRef(new Set());
  const systemReduced = usePrefersReducedMotion();
  const reduced = reducedMotion || systemReduced;
  const active = animate && !reduced && size >= TIMING.minSize;
  const b = { ...ALL_ON, ...behaviours };
  const activeRef = useRef(active);
  activeRef.current = active;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const later = useCallback((fn, ms) => {
    const t = setTimeout(() => { timers.current.delete(t); fn(); }, ms);
    timers.current.add(t);
  }, []);

  // Add a class for `ms`, restarting the animation if already running.
  const pulse = useCallback((cls, ms) => {
    const el = svgRef.current;
    if (!el) return;
    el.classList.remove(cls);
    void el.getBoundingClientRect();
    el.classList.add(cls);
    later(() => el.classList.remove(cls), ms);
  }, [later]);

  const fire = useCallback((type) => {
    if (!activeRef.current) { onEventRef.current?.(type, { skipped: true }); return false; }
    const T = TIMING;
    if (type === "blink") pulse("is-blink", T.blink.duration);
    if (type === "doubleBlink") {
      pulse("is-blink", T.blink.duration);
      later(() => pulse("is-blink", T.blink.duration), T.blink.duration + T.blink.doubleGap);
    }
    if (type === "twitchLeft") pulse("is-twitch-l", T.twitch.duration);
    if (type === "twitchRight") pulse("is-twitch-r", T.twitch.duration);
    if (type === "greet") pulse("is-greet", T.greet.duration);
    if (type === "tilt") pulse("is-tilt", 700);
    if (type === "breathe") pulse("is-breathe-once", T.breathe.duration);
    onEventRef.current?.(type, { skipped: false });
    return true;
  }, [pulse, later]);

  useImperativeHandle(ref, () => ({
    blink: () => fire("blink"),
    doubleBlink: () => fire("doubleBlink"),
    twitchLeft: () => fire("twitchLeft"),
    twitchRight: () => fire("twitchRight"),
    greet: () => fire("greet"),
    tilt: () => fire("tilt"),
    breathe: () => fire("breathe"),
  }), [fire]);

  // Clear everything when going inactive / unmounting.
  useEffect(() => {
    if (active) return undefined;
    const el = svgRef.current;
    el?.classList.remove("is-blink", "is-twitch-l", "is-twitch-r", "is-greet", "is-tilt", "is-breathe-once");
    return undefined;
  }, [active]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);

  // 4. Greeting — once per mount, only if active at first paint.
  const greeted = useRef(false);
  useEffect(() => {
    if (greeted.current) return;
    greeted.current = true;
    if (active && b.greet) fire("greet");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1. Blink loop — random 3.5–7s, ~1 in 6 doubles.
  useEffect(() => {
    if (!active || !b.blink) return undefined;
    let t;
    const loop = () => {
      t = setTimeout(() => {
        fire(Math.random() < TIMING.blink.doubleChance ? "doubleBlink" : "blink");
        loop();
      }, rand(TIMING.blink.intervalMin, TIMING.blink.intervalMax));
    };
    loop();
    return () => clearTimeout(t);
  }, [active, b.blink, fire]);

  // 2. Ear twitch — each ear every 8–14s, never both at once.
  useEffect(() => {
    if (!active || !b.twitch) return undefined;
    let t;
    let last = 0;
    const loop = () => {
      t = setTimeout(() => {
        // Alternate-biased, independent; a single scheduler guarantees no overlap.
        const side = Math.random() < 0.5 ? "twitchLeft" : "twitchRight";
        if (performance.now() - last > TIMING.twitch.duration) fire(side);
        last = performance.now();
        loop();
      }, rand(TIMING.twitch.intervalMin, TIMING.twitch.intervalMax) / 2);
    };
    loop();
    return () => clearTimeout(t);
  }, [active, b.twitch, fire]);

  // 3. Eye tracking — spring toward pointer, max 1.2 units. Fine pointers only.
  useEffect(() => {
    const g = trackRef.current;
    if (!g) return undefined;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    if (!active || !b.track || coarse) { g.style.transform = ""; return undefined; }
    const { maxOffset, stiffness, damping } = TIMING.track;
    let tx = 0, ty = 0, x = 0, y = 0, vx = 0, vy = 0, raf = 0;
    const move = (ev) => {
      const r = svgRef.current?.getBoundingClientRect();
      if (!r) return;
      const dx = ev.clientX - (r.left + r.width / 2);
      const dy = ev.clientY - (r.top + r.height * 0.5);
      const len = Math.hypot(dx, dy) || 1;
      const k = Math.min(1, len / (r.width * 0.9)) * maxOffset;
      tx = (dx / len) * k; ty = (dy / len) * k;
    };
    const leave = () => { tx = 0; ty = 0; };
    const tick = () => {
      vx = (vx + (tx - x) * stiffness) * damping;
      vy = (vy + (ty - y) * stiffness) * damping;
      x += vx; y += vy;
      const m = Math.hypot(x, y);
      if (m > maxOffset) { x = (x / m) * maxOffset; y = (y / m) * maxOffset; }
      g.style.transform = `translate(${x.toFixed(3)}px, ${y.toFixed(3)}px)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("pointermove", move);
    document.documentElement.addEventListener("pointerleave", leave);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", move);
      document.documentElement.removeEventListener("pointerleave", leave);
      g.style.transform = "";
    };
  }, [active, b.track]);

  // 5. Hover tilt.
  const hoverOn = active && b.hover && hoverable;
  const enter = () => { if (hoverOn) svgRef.current?.classList.add("is-tilt"); };
  const leaveHover = () => svgRef.current?.classList.remove("is-tilt");

  if (size < TIMING.compactBelow) return <PandaMark size={size} ink={ink} face={face} title={title} />;

  const { face: f, earL, earR, patchL, patchR, eyeL, eyeR, pupilL, pupilR, blushL, blushR, nose } = G;
  const [lx, ly] = G.earBaseL;
  const [rx, ry] = G.earBaseR;
  const o = (x, y) => ({ transformOrigin: `${x}px ${y}px` });
  const a11y = title ? { role: "img", "aria-label": title } : { "aria-hidden": "true", focusable: "false" };
  const cls = ["pm-live", reduced && "is-reduced", !active && "is-static", active && b.breathe && "is-breathing"].filter(Boolean).join(" ");

  return (
    <svg ref={svgRef} className={cls} width={size} height={size} viewBox={G.viewBox}
      xmlns="http://www.w3.org/2000/svg" onPointerEnter={enter} onPointerLeave={leaveHover} {...a11y}>
      <g className="pm-greet" style={o(100, 100)}>
        <g className="pm-breathe" style={o(100, 100)}>
          <g className="pm-tilt" style={o(100, 100)}><circle {...f} fill={face} /></g>
          <g className="pm-ear-follow" style={o(100, 100)}>
            <g className="pm-ear-greet-l" style={o(lx, ly)}>
              <g className="pm-twitch-l" style={o(lx, ly)}><circle {...earL} fill={ink} /></g>
            </g>
            <g className="pm-ear-greet-r" style={o(rx, ry)}>
              <g className="pm-twitch-r" style={o(rx, ry)}><circle {...earR} fill={ink} /></g>
            </g>
          </g>
          <g className="pm-tilt" style={o(100, 100)}>
            <circle {...patchL} fill={ink} />
            <circle {...patchR} fill={ink} />
            <g ref={trackRef}>
              <g className="pm-blink" style={o(eyeL.cx, eyeL.cy)}><circle {...eyeL} fill={face} /><circle {...pupilL} fill={ink} /></g>
              <g className="pm-blink" style={o(eyeR.cx, eyeR.cy)}><circle {...eyeR} fill={face} /><circle {...pupilR} fill={ink} /></g>
            </g>
            <circle {...blushL} fill={G.blush} opacity={G.blushOpacity} />
            <circle {...blushR} fill={G.blush} opacity={G.blushOpacity} />
            <ellipse {...nose} fill={ink} />
            <path d={G.mouth} fill="none" stroke={ink} strokeWidth={G.mouthStroke} />
          </g>
        </g>
      </g>
    </svg>
  );
}

export default PandaMarkLive;
